import { useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGE_BANDS,
  GUEST_SIDES,
  householdLabelFor,
  type AgeBand,
  type GuestSide,
} from '@guabi/shared';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { Field, SelectInput, TextInput } from '@/components/ui/Field';
import { ApiRequestError } from '@/lib/api';
import { useCreateHousehold } from './queries';

interface MemberDraft {
  /** Stable across re-renders. An array index here would re-bind inputs when a
   *  row is removed, and the typist would watch their text jump rows. */
  key: number;
  firstName: string;
  lastName: string;
  ageBand: AgeBand;
  unnamed: boolean;
}

const blank = (key: number): MemberDraft => ({
  key,
  firstName: '',
  lastName: '',
  ageBand: 'adult',
  unnamed: false,
});

/**
 * Adding a family in one go: one label, one address, then a row per person. This
 * is the flow for "my uncle, his wife and their two children", which as separate
 * guests would mean four forms and four addresses.
 */
export function HouseholdDrawer({
  wedId,
  open,
  onClose,
}: {
  wedId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateHousehold(wedId);
  const nextKey = useRef(2);

  const [label, setLabel] = useState('');
  const [side, setSide] = useState<GuestSide>('shared');
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [members, setMembers] = useState<MemberDraft[]>([blank(0), blank(1)]);
  const [error, setError] = useState<string>();

  /** Shown as the label's placeholder, so most people never have to type one. */
  const suggested = useMemo(
    () =>
      householdLabelFor(
        members
          .filter((m) => m.firstName.trim() && !m.unnamed)
          .map((m) => ({
            firstName: m.firstName.trim(),
            lastName: m.lastName.trim() || undefined,
          })),
        t('guests.householdFallback'),
      ),
    [members, t],
  );

  const patch = (key: number, next: Partial<MemberDraft>) =>
    setMembers((rows) => rows.map((r) => (r.key === key ? { ...r, ...next } : r)));

  const addRow = () => {
    setMembers((rows) => [...rows, blank(nextKey.current)]);
    nextKey.current += 1;
  };

  const removeRow = (key: number) =>
    setMembers((rows) => (rows.length > 1 ? rows.filter((r) => r.key !== key) : rows));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(undefined);

    const people = members
      .filter((m) => m.unnamed || m.firstName.trim())
      .map((m) =>
        m.unnamed
          ? { firstName: 'Guest', isPlaceholder: true, ageBand: m.ageBand, side }
          : {
              firstName: m.firstName.trim(),
              ...(m.lastName.trim() ? { lastName: m.lastName.trim() } : {}),
              ageBand: m.ageBand,
              side,
            },
      );

    if (people.length === 0) {
      setError(t('guests.householdNeedsOne'));
      return;
    }

    const address = {
      ...(line1 ? { line1 } : {}),
      ...(line2 ? { line2 } : {}),
      ...(city ? { city } : {}),
      ...(state ? { state } : {}),
      ...(postalCode ? { postalCode } : {}),
      ...(country ? { country } : {}),
    };

    try {
      await create.mutateAsync({
        ...(label.trim() ? { label: label.trim() } : { label: suggested }),
        side,
        ...(Object.keys(address).length > 0 ? { address } : {}),
        members: people,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiRequestError && err.code === 'entitlement_required'
          ? t('guests.capReached')
          : t('common.somethingWrong'),
      );
    }
  }

  return (
    /*
     * The primary action is pinned, not inline at the end of the form.
     *
     * These drawers scroll, and a Save you have to go looking for is one people
     * stop trusting is there — it also sat among other buttons that look the
     * same, which is how you press the wrong one. `form` is the attribute that
     * lets a button outside the form submit it.
     */
    <Drawer
      open={open}
      title={t('guests.householdAdd')}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" form="add-household" loading={create.isPending}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      }
    >
      <form id="add-household" onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t('guests.householdLabel')} hint={t('guests.householdLabelHint')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={200}
              placeholder={suggested}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('guests.side')} hint={t('guests.householdSideHint')}>
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              aria-describedby={describedBy}
              value={side}
              onChange={(e) => setSide(e.target.value as GuestSide)}
            >
              {GUEST_SIDES.map((s) => (
                <option key={s} value={s}>
                  {t(`guests.sideValue.${s}`)}
                </option>
              ))}
            </SelectInput>
          )}
        </Field>

        <fieldset className="flex flex-col gap-3 border-0 p-0">
          <legend className="text-caption font-medium text-ink">{t('guests.members')}</legend>

          {members.map((m, i) => (
            <div key={m.key} className="flex flex-col gap-2 border-b border-line pb-3">
              <div className="flex flex-wrap gap-2">
                <TextInput
                  className="min-w-[8rem] flex-1"
                  aria-label={`${t('guests.firstName')} ${i + 1}`}
                  placeholder={t('guests.firstName')}
                  maxLength={200}
                  disabled={m.unnamed}
                  value={m.firstName}
                  onChange={(e) => patch(m.key, { firstName: e.target.value })}
                />
                <TextInput
                  className="min-w-[8rem] flex-1"
                  aria-label={`${t('guests.lastName')} ${i + 1}`}
                  placeholder={t('guests.lastName')}
                  maxLength={200}
                  disabled={m.unnamed}
                  value={m.lastName}
                  onChange={(e) => patch(m.key, { lastName: e.target.value })}
                />
                <SelectInput
                  className="min-w-[7rem] flex-none"
                  aria-label={`${t('guests.ageBand')} ${i + 1}`}
                  value={m.ageBand}
                  onChange={(e) => patch(m.key, { ageBand: e.target.value as AgeBand })}
                >
                  {AGE_BANDS.map((a) => (
                    <option key={a} value={a}>
                      {t(`guests.ageBandValue.${a}`)}
                    </option>
                  ))}
                </SelectInput>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-caption text-leaf-soft">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-line"
                    checked={m.unnamed}
                    onChange={(e) => patch(m.key, { unnamed: e.target.checked })}
                  />
                  {t('guests.unnamedYet')}
                </label>
                {members.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(m.key)}
                    className="ml-auto min-h-tap text-caption text-status-declinedFg underline underline-offset-4"
                  >
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          ))}

          <Button type="button" variant="secondary" onClick={addRow}>
            {t('guests.addPerson')}
          </Button>
        </fieldset>

        <details className="rounded border border-line p-3">
          <summary className="cursor-pointer text-caption font-medium text-ink">
            {t('guests.mailingAddress')}
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            <Field label={t('guests.line1')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  maxLength={200}
                  value={line1}
                  onChange={(e) => setLine1(e.target.value)}
                />
              )}
            </Field>
            <Field label={t('guests.line2')}>
              {({ id }) => (
                <TextInput
                  id={id}
                  maxLength={200}
                  value={line2}
                  onChange={(e) => setLine2(e.target.value)}
                />
              )}
            </Field>
            <div className="flex flex-wrap gap-3">
              <Field label={t('guests.city')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('guests.state')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={200}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="flex flex-wrap gap-3">
              <Field label={t('guests.postalCode')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={20}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                )}
              </Field>
              <Field label={t('guests.country')}>
                {({ id }) => (
                  <TextInput
                    id={id}
                    maxLength={60}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>
        </details>

        {error && (
          <p role="alert" className="text-body text-status-declinedFg">
            {error}
          </p>
        )}
      </form>
    </Drawer>
  );
}
